import React, { useEffect, useMemo, useState } from "react";
import { Network, RefreshCcw } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

/**
 * AdminEcosystemMap — read-only force-directed-ish topology view.
 * Pulled live from /api/admin/topology. No dependency on any graph library:
 * we lay out nodes by type in concentric rings to keep the bundle slim.
 */

const TYPE_RINGS = [
  { type: "zone",      radius: 90,  color: "#b6ff1a" },
  { type: "franchise", radius: 170, color: "#a78bfa" },
  { type: "partner",   radius: 260, color: "#34d399" },
  { type: "driver",    radius: 350, color: "#60a5fa" },
  { type: "order",     radius: 430, color: "#f97316" },
  { type: "affiliate", radius: 510, color: "#f472b6" },
];

const TYPE_FILTERS = TYPE_RINGS.map((r) => r.type);

function layout(nodes) {
  // Returns {id → {x,y,type}}
  const grouped = {};
  for (const n of nodes) (grouped[n.type] = grouped[n.type] || []).push(n);
  const pos = {};
  for (const ring of TYPE_RINGS) {
    const list = grouped[ring.type] || [];
    const r = ring.radius;
    list.forEach((n, i) => {
      const theta = (i / Math.max(list.length, 1)) * Math.PI * 2;
      pos[n.id] = { x: r * Math.cos(theta), y: r * Math.sin(theta), type: n.type, color: ring.color, node: n };
    });
  }
  return pos;
}

export default function AdminEcosystemMap() {
  const [topo, setTopo] = useState(null);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState(new Set(TYPE_FILTERS));
  const [onlyAlerts, setOnlyAlerts] = useState(false);
  const [selected, setSelected] = useState(null);

  const reload = async () => {
    setErr("");
    try { const { data } = await api.get("/admin/topology"); setTopo(data); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); }, []);

  const toggleFilter = (t) => {
    const next = new Set(filter);
    next.has(t) ? next.delete(t) : next.add(t);
    setFilter(next);
  };

  const { positions, visibleEdges, visibleNodes } = useMemo(() => {
    if (!topo) return { positions: {}, visibleEdges: [], visibleNodes: [] };
    const filtered = topo.nodes.filter((n) => filter.has(n.type) && (!onlyAlerts || n.status === "alert"));
    const positions = layout(filtered);
    const visIds = new Set(Object.keys(positions));
    const visibleEdges = topo.edges.filter((e) => visIds.has(e.from) && visIds.has(e.to));
    return { positions, visibleEdges, visibleNodes: filtered };
  }, [topo, filter, onlyAlerts]);

  if (err) return <div className="text-rose-300" data-testid="ecosystem-error">{err}</div>;
  if (!topo) return <div className="font-bebas tracking-[0.3em] text-lifted-dim">LOADING TOPOLOGY…</div>;

  const VB = 1200; // viewBox half-width

  return (
    <div data-testid="admin-ecosystem-map" className="space-y-6">
      <header className="fade-up flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow flex items-center gap-2"><Network className="h-3 w-3" /> Live topology</div>
          <h1 className="headline-xl">Ecosystem map</h1>
          <p className="text-lifted-dim text-sm mt-2 max-w-2xl">
            {topo.counts.zone} zones · {topo.counts.franchise} franchises · {topo.counts.partner} partners · {topo.counts.driver} drivers · {topo.counts.order} active orders · {topo.counts.affiliate} affiliates.
            <span className="text-rose-300 ml-2">{topo.alerts_total} alerts.</span>
          </p>
        </div>
        <button onClick={reload} className="btn-ghost" data-testid="ecosystem-refresh"><RefreshCcw className="h-3 w-3" /> Refresh</button>
      </header>

      <div className="glass-panel p-4 flex flex-wrap gap-2 items-center" data-testid="ecosystem-controls">
        <span className="text-xs text-lifted-mute mr-2 font-bebas tracking-[0.18em] uppercase">Show:</span>
        {TYPE_RINGS.map((r) => (
          <button key={r.type} onClick={() => toggleFilter(r.type)}
                  data-testid={`ecosystem-filter-${r.type}`}
                  className={`chip cursor-pointer ${filter.has(r.type) ? "chip--green" : ""}`}>
            <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: r.color }} />{r.type} ({topo.counts[r.type] || 0})
          </button>
        ))}
        <label className="flex items-center gap-2 ml-4 text-xs text-lifted-dim cursor-pointer">
          <input type="checkbox" checked={onlyAlerts} onChange={(e) => setOnlyAlerts(e.target.checked)} data-testid="ecosystem-filter-alerts" />
          Only show alerts
        </label>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 glass-panel p-2 overflow-hidden" data-testid="ecosystem-svg-wrap">
          <svg viewBox={`${-VB} ${-VB} ${2 * VB} ${2 * VB}`} className="w-full h-[640px]">
            {/* edges */}
            {visibleEdges.map((e, i) => {
              const a = positions[e.from], b = positions[e.to];
              if (!a || !b) return null;
              return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                           stroke="rgba(182,255,26,0.18)" strokeWidth={1.2} />;
            })}
            {/* nodes */}
            {visibleNodes.map((n) => {
              const p = positions[n.id]; if (!p) return null;
              const isAlert = n.status === "alert";
              const isSel = selected && selected.id === n.id;
              return (
                <g key={n.id} onClick={() => setSelected(n)} style={{ cursor: "pointer" }}>
                  <circle cx={p.x} cy={p.y} r={isSel ? 12 : 8}
                          fill={isAlert ? "#fb7185" : p.color}
                          stroke={isAlert ? "#fff1f2" : "rgba(255,255,255,0.2)"}
                          strokeWidth={isAlert ? 2 : 1} />
                  {isSel && <text x={p.x + 14} y={p.y + 4} fontSize={18} fill="#fff" style={{ fontFamily: "Anton, sans-serif" }}>{n.label}</text>}
                </g>
              );
            })}
          </svg>
        </div>

        <aside className="glass-panel p-5 space-y-3 max-h-[640px] overflow-y-auto" data-testid="ecosystem-node-details">
          <div className="eyebrow !text-lifted-text-dim">Node inspector</div>
          {!selected && <div className="text-lifted-mute text-xs">Click a node on the map to inspect.</div>}
          {selected && (
            <div className="space-y-2 text-sm" data-testid={`ecosystem-node-${selected.id.replace(":", "-")}`}>
              <div><span className="text-lifted-mute text-xs">Type:</span> <span className="chip">{selected.type}</span></div>
              <div><span className="text-lifted-mute text-xs">Label:</span> <span className="text-lifted-text">{selected.label}</span></div>
              <div><span className="text-lifted-mute text-xs">Status:</span> <span className={`chip ${selected.status === "alert" ? "" : "chip--green"}`}>{selected.status}</span></div>
              {selected.alert && <div className="text-rose-300 text-xs">Alert: {selected.alert}</div>}
              <details className="text-xs text-lifted-dim">
                <summary className="cursor-pointer">meta</summary>
                <pre className="whitespace-pre-wrap break-all">{JSON.stringify(selected.meta, null, 2)}</pre>
              </details>
              <div className="text-xs text-lifted-mute pt-2 border-t border-white/5">id: {selected.id}</div>
            </div>
          )}
        </aside>
      </div>

      <section className="glass-panel p-4 text-xs text-lifted-dim" data-testid="ecosystem-legend">
        <div className="font-bebas tracking-[0.2em] uppercase text-lifted-mute mb-2">Layer key</div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {TYPE_RINGS.map((r) => (
            <div key={r.type} className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: r.color }} />
              <span>{r.type}</span>
            </div>
          ))}
          <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full bg-rose-400" /> <span>alert</span></div>
        </div>
      </section>
    </div>
  );
}
